/**
 * useRouteCollectionId — extract a collection id from the current URL.
 *
 * Matches routes of the form:
 *   /collection/:id          (legacy back-compat)
 *   /dogfood/collection/:id  (legacy back-compat)
 *   /g/:id                   (current canonical member-only entity URL)
 *
 * Returns undefined if no collection is currently open. The hook subscribes
 * to both `popstate` and a lightly monkey-patched `history.pushState` so it
 * re-runs when react-router navigates within the SPA.
 *
 * This is kept out of @shared/client because the URL pattern is specific to
 * the viewer's current routing layout; once we stabilise we can move it.
 *
 * NOTE: this hook is intentionally NOT a thin wrapper around
 * `@/lib/urls#useActiveCollectionId` — that hook depends on react-router
 * context and this one doesn't (it's reused from contexts where the router
 * isn't available, like the `@shared/experimental/multi-upload` drag target).
 */
import { useEffect, useState } from "react";
function ensureHistoryPatched() {
    const h = window.history;
    if (h.__multi_upload_patched)
        return;
    h.__multi_upload_patched = true;
    const origPush = h.pushState.bind(h);
    const origReplace = h.replaceState.bind(h);
    h.pushState = function (...args) {
        const result = origPush(...args);
        window.dispatchEvent(new Event("multi-upload:locationchange"));
        return result;
    };
    h.replaceState = function (...args) {
        const result = origReplace(...args);
        window.dispatchEvent(new Event("multi-upload:locationchange"));
        return result;
    };
}
// ---------------------------------------------------------------------------
// Matcher
// ---------------------------------------------------------------------------
/**
 * Regex covering every URL shape under which the viewer renders a
 * single collection / group entity by ID:
 *
 *   /collection/:id            legacy
 *   /dogfood/collection/:id    legacy (dogfood deploy)
 *   /group/:id                 legacy
 *   /g/:id                     canonical (post URL-rename)
 *
 * NOT covered: the public slug URLs (`/{name}` and `/{name}/{slug}`).
 * Those resolve to an ID via an async API round-trip, which this
 * regex-only matcher can't do. The drag-upload affordance falls back
 * to "no active collection" while a slug URL is open — uploads have to
 * be initiated from inside the resolved view, which already lives at
 * the member-only `/g/{id}` URL.
 */
const COLLECTION_RE = /^\/(?:(?:dogfood\/)?collection|group|g)\/([^/?#]+)/;
function collectionIdFromPath(pathname) {
    const m = COLLECTION_RE.exec(pathname);
    return m ? m[1] : undefined;
}
// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------
export function useRouteCollectionId() {
    const [collectionId, setCollectionId] = useState(() => typeof window !== "undefined" ? collectionIdFromPath(window.location.pathname) : undefined);
    useEffect(() => {
        ensureHistoryPatched();
        const recompute = () => {
            setCollectionId(collectionIdFromPath(window.location.pathname));
        };
        window.addEventListener("popstate", recompute);
        window.addEventListener("multi-upload:locationchange", recompute);
        return () => {
            window.removeEventListener("popstate", recompute);
            window.removeEventListener("multi-upload:locationchange", recompute);
        };
    }, []);
    return collectionId;
}
